:host(c-input) {
  --_c-input-inactive-color: var(--c-tertiary-600);
  --_c-input-active-color: var(--c-primary-600);
  --_c-input-background-color: var(--c-transparent);
  --_c-input-text-color: var(--c-text-body);
  --_c-input-placeholder-color: var(--c-tertiary-400);
  --_c-input-shadow-active-color: var(--_c-input-active-color);
  --_c-input-shadow-background-color: var(--c-white);
  --_c-input-shadow-text-color: var(--_c-input-text-color);
  --_c-input-shadow-placeholder-color: var(--_c-input-placeholder-color);
  --_c-input-label-color: var(--_c-input-label-color);

  // private
  --_c-input-error-color: var(--c-error-600);
}

.visuallyhidden {
  border: 0;
  clip: rect(0 0 0 0);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  white-space: nowrap;
  width: 1px;
}

.c-input {
  $this: &;

  --_c-input-border-width: 1px;

  align-items: flex-start;
  border-radius: 4px;
  display: flex;
  flex: 1 1 auto;
  font-family: var(--c-font-family);
  font-size: 16px;
  letter-spacing: normal;
  max-width: 100%;
  text-align: left;

  label {
    color: var(--_c-input-inactive-color);
  }

  input,
  select,
  textarea {
    background: none;
    border: 0;
    display: inline-block;
    font-family: var(--c-font-family);
    font-size: 16px;
    line-height: 1;
    margin: 0;
    padding: 0;
    vertical-align: middle;
    white-space: normal;
  }

  input,
  textarea {
    box-sizing: content-box;
    background-color: transparent;
    border: none;
    color: var(--_c-input-text-color);
    flex: 1 1 auto;
    font-family: var(--c-font-family);
    font-size: 16px;
    line-height: 20px;
    max-width: 100%;
    min-width: 0;
    width: 100%;
  }

  input[type="checkbox"],
  input[type="radio"],
  select {
    box-sizing: border-box;
  }

  input[type="search"] {
    appearance: textfield;
    box-sizing: content-box;
  }

  ::-webkit-search-decoration {
    display: none;
  }

  textarea {
    margin-top: 4px;
    min-height: 44px;
    overflow: auto;
    padding: 8px 12px 8px 0;
    resize: vertical;
    vertical-align: top;
    white-space: pre-wrap;
  }

  input {
    max-height: 32px;
    padding: 8px 0;
  }

  input:focus,
  input:active,
  input[aria-expanded="true"],
  textarea:focus,
  textarea:active {
    outline: none;
  }

  .c-input__content {
    align-items: center;
    display: flex;
    width: 100%;

    slot {
      display: none;
    }

    c-icon-button {
      aspect-ratio: 1;
      margin-right: -6px;
    }
  }

  &__control {
    border-radius: inherit;
    color: currentColor;
    display: flex;
    flex-flow: column wrap;
    flex-grow: 1;
    gap: 8px;
    height: auto;
    min-width: 0;
    width: 100%;
    position: relative;
  }

  &__slot {
    align-items: stretch;
    background: transparent;
    border-radius: inherit;
    color: inherit;
    cursor: text;
    display: flex;
    min-height: 44px;
    padding: 0 12px;
    position: relative;
    transition: 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
    width: 100%;
  }

  &__field {
    align-items: center;
    display: flex;
    flex: 1 1 auto;
    gap: 8px;
    position: relative;

    input,
    textarea {
      flex: 1;
      outline: 0;
    }

    label {
      font-size: 16px;
      height: 20px;
      left: 0;
      letter-spacing: normal;
      line-height: 20px;
      max-width: 90%;
      min-height: 8px;
      overflow: hidden;
      pointer-events: none;
      position: absolute;
      right: auto;
      text-overflow: ellipsis;
      top: 12px;
      white-space: nowrap;

      &:not(.label-on-top) {
        transform-origin: top left;
        transform: translateX(var(--_c-input-label-position)) translateY(0)
          scale(1);
        transition: 0.3s cubic-bezier(0.25, 0.8, 0.5, 1) 0.08s;
      }

      span {
        color: var(--c-error-600);
      }
    }
  }

  &--label-on-top {
    --c-message-padding: 0;

    display: grid;
    gap: 4px;

    input::placeholder,
    textarea::placeholder {
      color: var(--_c-input-placeholder-color);
      opacity: 1;
    }

    label {
      font-size: 14px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      max-width: 100%;
      color: var(--_c-input-label-color);
    }

    #{$this} {
      &__field label {
        color: var(--_c-input-active-color);
        transform: translateY(-36px) translateX(-16px) scale(0.875);
      }
    }
  }

  &--select {
    input {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .c-input__slot {
      cursor: pointer;
    }
  }

  fieldset {
    background-color: var(--_c-input-background-color);
    border-collapse: collapse;
    border-color: var(--_c-input-inactive-color);
    border-radius: inherit;
    border-style: solid;
    border-width: var(--_c-input-border-width);
    inset: 0;
    margin: 0;
    padding: 0 0 0 8px;
    pointer-events: none;
    position: absolute;
    transition-duration: 0.15s;
    transition-property: color;
    transition-timing-function: cubic-bezier(0.25, 0.8, 0.25, 1);

    legend {
      float: none;
      line-height: 11px;
      margin-left: -1px;
      padding: 0;
      text-align: left;
      transition: width 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
      width: 0;

      &.active:not(.label-on-top) {
        width: var(--_c-input-legend-width);
      }
    }
  }

  &--shadow {
    #{$this} {
      &__slot {
        background-color: var(--_c-input-shadow-background-color);
        box-shadow: rgba(0, 0, 0, 0.15) 0 5px 15px 0;

        input,
        textarea {
          color: var(--_c-input-shadow-text-color);
        }

        &:focus-within {
          outline: 2px var(--_c-input-active-color) solid;

          c-icon {
            --c-icon-color: var(--_c-input-shadow-active-color) !important;
          }
        }
      }
    }

    input::placeholder,
    textarea::placeholder {
      color: var(--_c-input-shadow-placeholder-color);
      opacity: 1;
    }
  }

  &--disabled {
    opacity: 0.75;

    input,
    textarea,
    fieldset,
    label {
      --_c-input-error-color: var(--c-tertiary-500);

      color: var(--c-tertiary-500);
    }

    #{$this} {
      &__slot, &__slot * {
        cursor: not-allowed !important;
      }
    }
  }

  &--textarea {
    #{$this} {
      &__field {
        margin-right: -12px;
      }
    }
  }

  ::placeholder {
    color: var(--_c-input-placeholder-color);
    opacity: 1;
  }

  &:focus-within,
  &--active,
  &:not(.c-input--disabled):active {
    --_c-input-border-width: 2px;

    color: var(--_c-input-active-color);

    fieldset {
      border-color: var(--_c-input-active-color);
    }

    label:not(.label-on-top) {
      color: var(--_c-input-active-color);
      transform: translateX(0) translateY(-18px) scale(0.75);
    }

    legend:not(.label-on-top) {
      width: var(--_c-input-legend-width);
    }
  }

  label:not(.label-on-top).filled {
    transform: translateX(0) translateY(-18px) scale(0.75);
  }

  &.active:not(.filled) {
    .c-input-menu__input {
      position: relative;

      &::after {
        display: flex;
        align-items: center;
        position: absolute;
        inset: 0;
        content: attr(data-placeholder);
        pointer-events: none;
        color: var(--_c-input-placeholder-color);
      }
    }
  }

  &--error {
    color: var(--c-error-600);

    fieldset {
      border-color: var(--c-error-600) !important;
    }

    label {
      color: var(--c-error-600) !important;
    }

    &:focus-within {
      color: var(--c-error-600);
    }
  }
}
